/**
 * This file defines the requestAccess() service.
 */

import type { GasPurePayload } from '@atlaskit/analytics-gas-types';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { AnyAri } from '@atlassian/ari/any-ari';

import type {
	AbstractServiceMarker,
	AnyServiceWithDeps,
	DependenciesContext,
} from '../../core/types';
import { toError } from '../../core/utils';
import {
	createAnalyticsEvent,
	getCurrentCloudId,
	getCurrentProduct,
	getCurrentUserId,
	getStargateURL,
	type Product,
} from '../common/common';
import { type FetchInput, type FetchResponse, fetchJSON } from '../common/fetchJSON';

export type RequestAccessResult = RequestAccessInput & {
	resource: string;
	traceId?: string;
	userId: string;
} & (
		| { error: null; result: AccessRequestType }
		| {
				error: Error;
				result: null;
		  }
	);

export type AccessRequestType =
	| 'ACCESS_GRANTED'
	| 'PENDING_REQUEST_CREATED'
	| 'ACCESS_EXISTS'
	| 'VERIFICATION_REQUIRED'
	| 'PENDING_REQUEST_EXISTS'
	| 'DENIED_REQUEST_EXISTS'
	| 'APPROVED_REQUEST_EXISTS'
	| 'FORBIDDEN';

export type RequestAccessResponse = {
	results: {
		[resource: string]: AccessRequestType;
	};
};

/**
 * Valid values are defined in IIS:
 * https://bitbucket.org/atlassian/id-invitations-service/src/master/src/main/java/com/atlassian/identity/invitations/model/EnablementTouchpoint.java
 */
const EnablementTouchpoint = {
	CROSSJOIN_NUDGE: 'Crossjoin Nudge',
} as const;

type EnablementTouchpoint = (typeof EnablementTouchpoint)[keyof typeof EnablementTouchpoint];

// Input - data directly needed to join/raise a specific RA:
export type RequestAccessInput = {
	cloudId?: string; // If not given, defaults to dependencies.getCurrentCloudId()
	integration: string;
	note?: string;
	product: Product;
	touchpoint?: EnablementTouchpoint;
};

// Dependencies - generally common to all RA's/joins:
const dependencies: {
	createAnalyticsEvent: CreateUIAnalyticsEvent & AbstractServiceMarker;
	fetchJSON: (<Response>(input: FetchInput) => Promise<FetchResponse<Response>>) &
		AbstractServiceMarker;
	getCurrentCloudId: (() => string) & AbstractServiceMarker;
	getCurrentProduct: (() => Product) & AbstractServiceMarker;
	getCurrentUserId: (() => string) & AbstractServiceMarker;
	getStargateURL: (url: `/${string}`) => string;
} = {
	fetchJSON,
	createAnalyticsEvent,
	getStargateURL,
	getCurrentUserId,
	getCurrentCloudId,
	getCurrentProduct,
};

type Deps = typeof dependencies;

export const requestAccess: typeof requestAccessImpl & {
	dependencies: {
		createAnalyticsEvent: CreateUIAnalyticsEvent & AbstractServiceMarker;
		fetchJSON: (<Response>(input: FetchInput) => Promise<FetchResponse<Response>>) &
			AbstractServiceMarker;
		getCurrentCloudId: (() => string) & AbstractServiceMarker;
		getCurrentProduct: (() => Product) & AbstractServiceMarker;
		getCurrentUserId: (() => string) & AbstractServiceMarker;
		getStargateURL: (url: `/${string}`) => string;
	};
} = Object.assign(requestAccessImpl, {
	dependencies,
}) satisfies AnyServiceWithDeps<Deps>;

async function requestAccessImpl(
	input: RequestAccessInput,
	context: DependenciesContext<Deps>,
): Promise<RequestAccessResult> {
	const deps = context.dependencies();
	const cloudId = input.cloudId ?? deps.getCurrentCloudId();
	const userId = deps.getCurrentUserId();
	let result: RequestAccessResult;
	const resource = AnyAri.create({
		resourceType: 'site',
		resourceId: cloudId,
		resourceOwner: input.product,
	}).toString();
	try {
		// https://developer.atlassian.com/platform/identity/rest/v1/api-group-access-requests/#api-v2-access-requests-request-post
		const fetchResponse = await deps.fetchJSON<RequestAccessResponse>({
			url: deps.getStargateURL('/invitations/v1/access-requests/request'),
			method: 'POST',
			credentials: 'include',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				resource,
				...(input.note && fg('puma-1287-add-note-payload') ? { note: input.note } : {}),
				metadata: {
					sourceProduct: deps.getCurrentProduct(),
					...(input.touchpoint ? { touchpoint: input.touchpoint } : {}),
				},
			}),
		});
		const flagEnabled = fg('expansion-services-trace-id');
		const gatedTraceId = fetchResponse.traceId && flagEnabled ? fetchResponse.traceId : undefined;
		result = mapRequestAccessResponse(fetchResponse.data, {
			resource,
			userId,
			...input,
			traceId: gatedTraceId,
		});
	} catch (error) {
		result = { error: toError(error), ...input, result: null, resource, userId };
	}
	fireRequestAccessAnalytics(result, context);
	return result;
}

function mapRequestAccessResponse(
	response: RequestAccessResponse,
	input: RequestAccessInput & { resource: string; traceId?: string; userId: string },
): RequestAccessResult {
	return {
		error: null,
		result: response.results[input.resource],
		...input,
	};
}

// Analytics functions for these common events might later become
// exported for standalone use
function fireRequestAccessAnalytics(
	{ error, product, result, traceId }: RequestAccessResult,
	context: DependenciesContext<Deps>,
) {
	if (!error) {
		const currentUserId = context.dependencies().getCurrentUserId();
		const attributes: AnalyticsAttributes = {
			userAaid: currentUserId,
			actorAaid: currentUserId,
			roleName: 'product/member',
			inviteStatus: result,
			sourceProduct: context.dependencies().getCurrentProduct(),
			sourceTenantId: context.dependencies().getCurrentCloudId(),
			...(fg('puma-1344-request-access-attributes')
				? { PUMA_1344_REQUEST_ACCESS_ATTRIBUTES: true, targetProduct: product }
				: { PUMA_1344_REQUEST_ACCESS_ATTRIBUTES: false, product }),
			...(traceId ? { traceId } : {}),
		};
		if (result === 'ACCESS_GRANTED') {
			fireProductJoined(attributes, context);
		} else {
			fireProductAccessRequested(attributes, context);
		}
	}
}

type UserRole =
	| 'product/member'
	| 'product/helpseeker'
	| 'product/customer'
	| 'product/external-collaborator-writer'
	| 'product/anonymous'
	| 'product/unlicensed'; // TODO flesh out admin roles etc.

type FeatureGatedProductAttribute =
	| {
			product: Product; // Product being requested to
			PUMA_1344_REQUEST_ACCESS_ATTRIBUTES: false;
	  }
	| {
			PUMA_1344_REQUEST_ACCESS_ATTRIBUTES: true;
			targetProduct: Product; // Product being requested to
	  };

type AnalyticsAttributes = {
	actorAaid: string; // AAID of user initiating request (same as above if RA1/3, different if RA2)
	actorRoleName?: UserRole; // Role of the user initiating the request
	inviteStatus: AccessRequestType; // USER_EXISTS, INVITED_PENDING_APPROVAL
	recommendationSources?: string[]; // Source of user invited (eg Google, Slack, Microsoft, Collab Graph?)
	roleName: UserRole; // Role being requested for the user
	sourceProduct: Product; // Product being initiated from (in case of server event with different )
	sourceTenantId: string; // TenantID of the product where the request was initiated
	traceId?: string; // Trace ID from the response, gated behind expansion-services-trace-id
	userAaid: string; // AAID of user who the request is for
	userUuid?: string; // For 3P, if we don't have AAID yet: UUID of user who the request is for
	//...originAttributes TODO?
} & FeatureGatedProductAttribute;

function fireProductAccessRequested(
	input: AnalyticsAttributes,
	context: DependenciesContext<Deps>,
) {
	const { PUMA_1344_REQUEST_ACCESS_ATTRIBUTES, ...attributes } = input;
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				actionSubject: 'productAccess',
				action: 'requested',
				actionSubjectId: PUMA_1344_REQUEST_ACCESS_ATTRIBUTES ? input.targetProduct : input.product,
				attributes,
			} satisfies GasPurePayload,
		})
		.fire();
}

function fireProductJoined(input: AnalyticsAttributes, context: DependenciesContext<Deps>) {
	const {
		userUuid,
		userAaid,
		roleName,
		sourceTenantId,
		sourceProduct,
		recommendationSources,
		PUMA_1344_REQUEST_ACCESS_ATTRIBUTES,
	} = input;
	context
		.dependencies()
		.createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				actionSubject: 'product',
				action: 'joined',
				actionSubjectId: PUMA_1344_REQUEST_ACCESS_ATTRIBUTES ? input.targetProduct : input.product,
				attributes: {
					userUuid,
					userAaid,
					roleName,
					sourceTenantId,
					sourceProduct,
					recommendationSources,
					...(PUMA_1344_REQUEST_ACCESS_ATTRIBUTES
						? { targetProduct: input.targetProduct }
						: { product: input.product }),
					...(input.traceId ? { traceId: input.traceId } : {}),
				},
			} satisfies GasPurePayload,
		})
		.fire();
}
